import { Location } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { type PointerEvent as ReactPointerEvent, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COBLI_ACTION_EVENT, type CobliActionEventDetail } from '../../lib/cobliLinkActions';
import { useAIAssistant } from '../../store';
import AIAssistantPanelContent from './AIAssistantPanelContent';

const AI_ROUTE = '/ai';
const MIN_AISIDEPANEL_WIDTH = 360;
const MAX_AISIDEPANEL_WIDTH = 840;
const MIN_MAIN_CONTENT_WIDTH = 320;

const clampAISidepanelWidth = (width: number) => {
  if (typeof window === 'undefined') return width;

  const maxWidth = Math.min(MAX_AISIDEPANEL_WIDTH, Math.max(0, window.innerWidth - MIN_MAIN_CONTENT_WIDTH));
  return Math.min(Math.max(width, MIN_AISIDEPANEL_WIDTH), maxWidth);
};

const AIAssistantSidepanel = () => {
  const { t } = useTranslation('ai');
  const { isAISidepanelOpen, aiSidepanelWidth, aiSidepanelLayout, setAISidepanelWidth, setAISidepanelLayout, closeAISidepanel } = useAIAssistant();
  const location = useLocation();
  const [isResizing, setIsResizing] = useState(false);
  const isAIRoute = location.pathname.startsWith(AI_ROUTE);
  const isMapLayoutRoute = location.pathname.startsWith(Location.MONITORING) || location.pathname.startsWith(Location.TRACKING);
  const isDrawer = aiSidepanelLayout ? aiSidepanelLayout === 'drawer' : isMapLayoutRoute;
  const width = clampAISidepanelWidth(aiSidepanelWidth);

  // When the AI agent emits a `#cobli-open-blip-chat` link, AIChatMarkdown
  // opens Blip and dispatches this event so the sidepanel closes in the same
  // gesture.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CobliActionEventDetail>).detail;
      if (detail?.action === 'open-blip-chat' && isAISidepanelOpen) {
        closeAISidepanel();
      }
    };
    window.addEventListener(COBLI_ACTION_EVENT, handler);
    return () => window.removeEventListener(COBLI_ACTION_EVENT, handler);
  }, [isAISidepanelOpen, closeAISidepanel]);

  useEffect(() => {
    if (width !== aiSidepanelWidth) setAISidepanelWidth(width);
  }, [aiSidepanelWidth, setAISidepanelWidth, width]);

  useEffect(() => {
    const handleResize = () => {
      setAISidepanelWidth(clampAISidepanelWidth(aiSidepanelWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [aiSidepanelWidth, setAISidepanelWidth]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const handlePointerMove = (event: PointerEvent) => {
      const nextWidth = clampAISidepanelWidth(window.innerWidth - event.clientX);
      setAISidepanelWidth(nextWidth);
    };

    const stopResizing = () => setIsResizing(false);

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
    };
  }, [isResizing, setAISidepanelWidth]);

  const handleResizeStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
  };

  const handleToggleSidepanelLayout = () => {
    setAISidepanelLayout(isDrawer ? 'inline' : 'drawer');
  };

  if (isAIRoute) return null;
  if (!isAISidepanelOpen) return null;

  return (
    <aside
      key='ai-sidepanel'
      data-testid='ai-sidepanel'
      data-layout={isDrawer ? 'drawer' : 'inline'}
      style={{ width }}
      className={cn(
        'flex h-screen-without-header flex-col bg-gray-2 pl-1',
        isDrawer ? 'slide-in-from-right fixed top-[var(--navbar-height)] right-0 z-[9002] max-w-full animate-in duration-300' : 'relative shrink-0',
        isResizing && 'select-none'
      )}
    >
      <button
        type='button'
        aria-label={t('sidepanel.resize_aria_label')}
        onPointerDown={handleResizeStart}
        className='group absolute top-0 left-0 z-10 flex h-full w-[4px] cursor-col-resize items-center justify-center'
      >
        <div className={cn('h-full w-[4px] rounded-full bg-transparent transition-colors group-hover:bg-primary-9', isResizing && 'bg-primary-8')} />
      </button>
      <div className='flex flex-1 flex-col overflow-hidden border-gray-6 border-l shadow-lg'>
        <Suspense
          fallback={
            <div className='flex flex-1 items-center justify-center'>
              <Icon name='loader' size='md' className='animate-spin' />
            </div>
          }
        >
          <AIAssistantPanelContent variant='sidepanel' isSidepanelDrawer={isDrawer} onToggleSidepanelLayout={handleToggleSidepanelLayout} onClose={closeAISidepanel} />
        </Suspense>
      </div>
    </aside>
  );
};

export default AIAssistantSidepanel;
